'use client';

import { Fragment } from 'react';
import { ExpandableCallout, Icon, iconNameSet, type IconName } from '@galapagos/design-system/web';
import { ensureBaselineStyle } from '../baseline/baseline-style';
import type { BaselineUnitGuide } from '../baseline/baseline-wording';

/** A served icon name is only drawn when the design system has it. */
function isIconName(name: string): name is IconName {
  return iconNameSet.has(name);
}

/**
 * The body of the "what counts as one" panel: a row of equivalents separated
 * by "=" (drawn by the stylesheet, not copy), or a sentence.
 */
export function UnitGuide({ guide }: { guide: BaselineUnitGuide }) {
  ensureBaselineStyle();
  if (guide.kind === 'text') return <p className="signup-baseline__guide-text">{guide.body}</p>;
  return (
    <ul className="signup-baseline__guide" data-testid="signup-baseline-week-guide">
      {guide.items.map((item, index) => (
        <Fragment key={`${index}-${item.label}`}>
          {index > 0 ? <li className="signup-baseline__equals" aria-hidden="true" /> : null}
          <li className="signup-baseline__equivalent">
            {isIconName(item.icon) ? <Icon name={item.icon} aria-hidden /> : null}
            <span>{item.label}</span>
            {item.sublabel ? (
              <span className="signup-baseline__equivalent-sublabel">{item.sublabel}</span>
            ) : null}
          </li>
        </Fragment>
      ))}
    </ul>
  );
}

/**
 * The collapsed "what counts as one" panel a counted baseline step shows
 * above its days, so every step reads the unit the same way.
 */
export function UnitGuideCallout({
  guide,
  'data-testid': testId,
}: {
  guide: BaselineUnitGuide;
  'data-testid': string;
}) {
  return (
    <ExpandableCallout
      title={guide.title}
      actionId="signup.baseline-guide.toggle"
      data-testid={testId}
    >
      <UnitGuide guide={guide} />
    </ExpandableCallout>
  );
}
